import axios from 'axios';
// import { logout } from './services/authService';  // A service to handle logout
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { ErrorHandlingService } from './errorHandler';
import {
  convertLocalToUTC,
  convertUTCToLocal,
} from './components/utils/dateUtils';
import { AUTH_PERMISSIONS_STALE_EVENT } from './constants/authEvents';
import { shouldSkipAuthRedirect, isLoginRoute } from './utils/routeUtils';

const axiosInstance = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: process.env.REACT_APP_SERVER_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// TODO: Have single logout function in one place
const logout = async () => {
  // Clear any pending toasts
  toast.dismiss();

  try {
    // Attempt to call logout API endpoint
    const response = await axiosInstance.get('/api/auth/logout', {
      // Don't retry on 401 errors to avoid loops
      validateStatus: (status) => status === 200 || status === 401,
    });

    // Redirect to login regardless of response status
    // (even if logout API call fails, we still want to redirect)
    if (response.status === 200 || response.status === 401) {
      window.location.href = '/login';
    }
  } catch (error) {
    // If logout API call fails (network error, etc.), still redirect to login
    console.log('Logout API call failed:', error);
    window.location.href = '/login';
  }
};

// Add request interceptor to handle outgoing requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Skip FormData - don't iterate over it as it can corrupt the data
    if (config.data && !(config.data instanceof FormData)) {
      // Process all data in the request payload
      for (const key in config.data) {
        if (
          typeof config.data[key] === 'string' &&
          Date.parse(config.data[key] && config.data[key] instanceof Date)
        ) {
          // Convert Date objects to UTC ISO string
          config.data[key] = convertLocalToUTC(config.data[key]);
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle 401 errors globally
// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data) {
      // Process all data in the response payload
      for (const key in response.data) {
        if (
          typeof response.data[key] === 'string' &&
          Date.parse(response.data[key]) &&
          response.data[key] instanceof Date
        ) {
          // Convert UTC date strings to local timezone
          response.data[key] = convertUTCToLocal(response.data[key]);
        }
      }
    }
    return response;
  },
  async (error) => {
    const statusCode = error.response?.status;
    const requestUrl = error.config?.url;

    // When responseType is 'blob', error responses are also Blobs — parse them back to JSON
    let data = error.response?.data;
    if (data instanceof Blob) {
      try {
        data = JSON.parse(await data.text());
      } catch {
        data = {};
      }
    }

    const errorMessage =
      data?.error?.message || data?.message || 'An error occurred';

    // Skip handling 401 for logout endpoint to prevent infinite loop
    if (statusCode === 401 && requestUrl?.includes('/api/auth/logout')) {
      return Promise.reject(error);
    }

    // Log the error
    ErrorHandlingService.logError(error, {
      statusCode,
      errorMessage,
      url: requestUrl,
    });

    // Handle different error types
    switch (statusCode) {
      case 401:
        // Unauthorized - call logout function to handle cleanup and redirect
        // Skip redirect for public pages that expect 401 responses
        if (!shouldSkipAuthRedirect()) {
          logout();
        }
        break;

      case 403:
        // Route-level protection is handled by AuthorizedRoute (frontend guard).
        // If a 403 reaches here, it's a backend rejection for a specific API call.
        // Dispatch invalidation so AuthContext re-fetches (permissions may be stale).
        // Let the promise reject — the calling component decides how to handle it.
        window.dispatchEvent(new CustomEvent(AUTH_PERMISSIONS_STALE_EVENT));
        break;

      case 404: {
        const fileNotFound =
          data?.error?.type === 'FileNotFoundError' ||
          data.error?.resource === 'File';
        if (fileNotFound) {
          console.log('Requested attachment could not be found:', error);
        } else {
          toast.error(t('error.notFound'), { position: 'top-center' });
          window.location.href = '/error/404';
        }
        break;
      }

      case 500:
        // Server Error
        toast.error(t('error.server'), { position: 'top-center' });
        //  window.location.href = '/error/500';
        break;

      case 400:
        // Bad Request - let components handle these errors themselves
        // Don't show automatic toast to avoid duplicates
        break;

      default:
        // Handle other errors
        if (!error.response) {
          // Network error
          toast.error(t('error.network'), { position: 'top-center' });
        } else {
          toast.error(t(errorMessage), { position: 'top-center' });
        }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
